<template>
    <generic-page>
        <back-to-dashboard />

        <div class="d-flex align-items-center justify-content-between mt-2 mb-1">
            <h5 class="mb-0">Spotlight</h5>
            <b-btn variant="light" pill size="sm" @click="helpModal = true">
                <font-awesome-icon icon="fa-duotone fa-info-circle" class="mr-1" />
                How it Works
            </b-btn>
        </div>
        <div class="text-secondary small mb-3">
            Weekly video competition — top videos win cash prizes
        </div>

        <div v-if="spotlight">
            <div class="countdown-card">
                <font-awesome-icon
                    icon="fa-duotone fa-clock"
                    class="mr-2"
                />
                <strong v-if="spotlight.defaultMap.daysRemaining === 1">
                    Last day!
                </strong>
                <strong v-else>
                    {{ spotlight.defaultMap.daysRemaining }} days left
                </strong>
                <span class="text-secondary ml-1">— resets Friday</span>
            </div>

            <div class="section-label">Prizes</div>
            <div class="prize-grid">
                <div
                    v-for="prize in spotlight.prizesDisplay"
                    :key="prize.position"
                    class="prize-slot"
                    :class="{ 'prize-first': prize.position === 1 }"
                >
                    <div class="prize-position">{{ ordinal(prize.position) }}</div>
                    <coin-badge :amount="prize.credits" />
                    <div class="prize-local">{{ prize.local }}</div>
                </div>
            </div>

            <div class="section-label">Leaderboard</div>
            <div v-if="spotlight.leaderboard && spotlight.leaderboard.length">
                <div
                    v-for="(entry, index) in spotlight.leaderboard"
                    :key="entry.creatorVideoId"
                    class="leaderboard-row"
                >
                    <div class="leaderboard-rank">
                        <span class="rank-number">{{ index + 1 }}</span>
                    </div>
                    <div class="leaderboard-thumb-wrap">
                        <img
                            v-if="entry.creatorVideo.previewUrl || entry.creatorVideo.thumbnailUrl"
                            :src="entry.creatorVideo.previewUrl || entry.creatorVideo.thumbnailUrl"
                            class="leaderboard-thumb"
                        />
                        <div v-else class="leaderboard-thumb-placeholder">
                            <font-awesome-icon
                                icon="fa-duotone fa-video"
                                class="text-secondary"
                            />
                        </div>
                    </div>
                    <div class="leaderboard-info">
                        <div class="d-flex align-items-center">
                            <b-badge
                                v-if="isMe(entry.userId)"
                                variant="primary"
                                pill
                            >
                                You
                            </b-badge>
                            <b-badge
                                v-else
                                variant="light"
                                pill
                                class="border"
                            >
                                {{ entry.codename }}
                            </b-badge>
                        </div>
                        <div
                            v-if="entry.creatorVideo.message"
                            class="leaderboard-message text-truncate"
                        >
                            {{ entry.creatorVideo.message }}
                        </div>
                    </div>
                    <div class="leaderboard-prize">
                        <coin-badge
                            v-if="spotlight.prizesDisplay[index]"
                            :amount="spotlight.prizesDisplay[index].credits"
                        />
                    </div>
                </div>
            </div>
            <div v-else class="empty-state">
                <font-awesome-icon
                    icon="fa-duotone fa-stars"
                    size="2x"
                    class="text-secondary mb-2"
                />
                <div>No videos this week yet</div>
                <div class="text-secondary small">
                    Be the first to upload!
                </div>
            </div>

            <div class="section-label">Your Videos</div>
            <b-btn
                to="/creator/creatorVideo"
                variant="primary"
                block
                pill
                class="manage-btn"
            >
                <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center">
                        <font-awesome-icon
                            icon="fa-duotone fa-camcorder"
                            class="mr-2"
                        />
                        Manage Videos
                    </div>
                    <font-awesome-icon
                        icon="fa-light fa-chevron-right"
                        class="text-white"
                    />
                </div>
            </b-btn>
            <div class="text-secondary small mt-1 px-1">
                Your published videos are automatically entered each week
            </div>
        </div>
        <div v-else>
            <generic-loading />
        </div>

        <b-modal v-model="helpModal" hide-footer centered title="How Spotlight Works">
            <div class="mb-3">
                <strong>Weekly Competition</strong>
                <div class="small text-secondary">
                    Every week, the top 5 videos with the most upvotes win cash
                    prizes. The competition resets every Friday at midnight UTC.
                </div>
            </div>
            <div class="mb-3">
                <strong>How to Enter</strong>
                <div class="small text-secondary">
                    Just upload and publish a video — you're automatically
                    entered. You can publish one video per day.
                </div>
            </div>
            <div class="mb-3">
                <strong>How Ranking Works</strong>
                <div class="small text-secondary">
                    Rankings are based on upvotes from customers watching your
                    videos. Votes from active, paying members count
                    significantly more than free accounts.
                </div>
            </div>
            <div class="mb-3">
                <strong>Prizes</strong>
                <div class="small text-secondary">
                    1st place: $50, 2nd: $20, 3rd: $15, 4th: $10, 5th: $5.
                    Prizes are awarded when the week ends.
                </div>
            </div>
            <div>
                <strong>
                    <font-awesome-icon icon="fa-solid fa-ban" class="mr-1 text-danger" />
                    Fair Play
                </strong>
                <div class="small text-secondary">
                    Only votes from real customers count towards your ranking.
                    Using fake accounts will lower the ranking of your
                    video.
                </div>
            </div>
            <b-btn
                @click="helpModal = false"
                variant="primary"
                pill
                block
                class="mt-3"
            >
                Got it
            </b-btn>
        </b-modal>
    </generic-page>
</template>

<style scoped>
.section-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    padding: 16px 0 6px;
}

.countdown-card {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-radius: 10px;
    background: rgba(99, 102, 241, 0.08);
    border: 1px solid rgba(99, 102, 241, 0.15);
    font-size: 0.9rem;
}

.prize-grid {
    display: flex;
    gap: 8px;
}

.prize-slot {
    flex: 1;
    text-align: center;
    padding: 10px 4px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.prize-first {
    background: rgba(234, 179, 8, 0.1);
    border-color: rgba(234, 179, 8, 0.25);
}

.prize-position {
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #94a3b8;
    font-weight: 600;
    letter-spacing: 0.03em;
}

.prize-local {
    font-size: 0.6rem;
    color: #64748b;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.leaderboard-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.leaderboard-row:last-child {
    border-bottom: none;
}

.leaderboard-rank {
    flex-shrink: 0;
    width: 28px;
    text-align: center;
}

.rank-number {
    font-size: 1.1rem;
    font-weight: 700;
    color: #94a3b8;
}

.leaderboard-row:first-child .rank-number {
    color: #eab308;
}

.leaderboard-row:nth-child(2) .rank-number {
    color: #a1a1aa;
}

.leaderboard-row:nth-child(3) .rank-number {
    color: #b45309;
}

.leaderboard-thumb-wrap {
    flex-shrink: 0;
    width: 40px;
    aspect-ratio: 9 / 16;
    border-radius: 4px;
    overflow: hidden;
    background: #1a1a1a;
}

.leaderboard-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.leaderboard-thumb-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.leaderboard-info {
    flex: 1;
    min-width: 0;
}

.leaderboard-message {
    font-size: 0.75rem;
    color: #94a3b8;
    margin-top: 2px;
}

.leaderboard-prize {
    flex-shrink: 0;
}


.empty-state {
    text-align: center;
    padding: 32px 16px;
}

.vote-hint {
    font-size: 0.75rem;
    color: #64748b;
    padding: 12px 0;
    text-align: center;
}

.manage-btn {
    padding: 14px 16px;
}
</style>

<script>
import { mapState } from "vuex";

import BackToDashboard from "@/components/utility/BackToDashboard";

export default {
    data() {
        return {
            helpModal: false,
        };
    },
    mounted() {
        this.$store.dispatch("spotlight/refresh");
    },
    computed: {
        ...mapState("spotlight", ["spotlight"]),
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
    },
    methods: {
        isMe(userId) {
            return this.onboardingUser?.id === userId;
        },
        ordinal(n) {
            const num = parseInt(n);
            const s = ["th", "st", "nd", "rd"];
            const v = num % 100;
            return num + (s[(v - 20) % 10] || s[v] || s[0]);
        },
    },
    components: {
        BackToDashboard,
    },
};
</script>
